import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  DEFAULT_COUNTRY_CODE,
  PRIORITY_ISO,
  getFlagEmoji,
  getSortedCountries,
  getCountryByDial,
} from "@/lib/country-codes";

function filterCountries(countries, query) {
  const q = query.trim().toLowerCase().replace(/^\+/, "");
  if (!q) return countries;
  return countries.filter((c) => {
    return (
      c.name.toLowerCase().includes(q) ||
      c.iso.toLowerCase().includes(q) ||
      c.dial.replace("+", "").startsWith(q) ||
      c.dial.toLowerCase().includes(q)
    );
  });
}

export function CountryCodeSelect({
  value,
  onChange,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectedRef = useRef(null);

  const sorted = useMemo(() => getSortedCountries(), []);
  const selected =
    getCountryByDial(value) ||
    getCountryByDial(DEFAULT_COUNTRY_CODE);

  const filtered = useMemo(() => filterCountries(sorted, search), [sorted, search]);

  const priorityFiltered = useMemo(() => {
    if (search.trim()) return [];
    return sorted.filter((c) => PRIORITY_ISO.includes(c.iso));
  }, [sorted, search]);

  const restFiltered = useMemo(() => {
    if (search.trim()) return filtered;
    return sorted.filter((c) => !PRIORITY_ISO.includes(c.iso));
  }, [sorted, filtered, search]);

  const handleSelect = (dial) => {
    onChange?.(dial);
    setOpen(false);
    setSearch("");
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  // When reopened, bring the selected country into view so users who
  // don't want to search can still find where they are in the full list.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => {
        selectedRef.current?.scrollIntoView({ block: "nearest" });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const isSearching = search.trim() !== "";
  const listCount = isSearching ? filtered.length : sorted.length;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "flex h-11 w-[112px] shrink-0 items-center justify-between gap-1 px-2 text-sm font-normal sm:h-8",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="text-base leading-none">
              {getFlagEmoji(selected?.iso)}
            </span>
            <span className="truncate">{selected?.dial || value}</span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[280px] overflow-hidden p-0"
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search country or code..."
            className="h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {isSearching && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {listCount}
            </span>
          )}
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {filtered.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No countries found
            </p>
          )}

          {priorityFiltered.length > 0 && (
            <>
              <SectionHeader label="Currency countries" />
              {priorityFiltered.map((c) => (
                <CountryRow
                  key={`p-${c.iso}-${c.dial}`}
                  country={c}
                  selected={selected?.dial === c.dial && selected?.iso === c.iso}
                  onSelect={() => handleSelect(c.dial)}
                  innerRef={
                    selected?.dial === c.dial && selected?.iso === c.iso
                      ? selectedRef
                      : null
                  }
                />
              ))}
              <div className="mx-2 my-1 h-px bg-border" />
              <SectionHeader
                label={`All countries (${restFiltered.length})`}
              />
            </>
          )}

          {search.trim() !== "" &&
            filtered.map((c) => (
              <CountryRow
                key={`s-${c.iso}-${c.dial}`}
                country={c}
                selected={selected?.dial === c.dial && selected?.iso === c.iso}
                onSelect={() => handleSelect(c.dial)}
              />
            ))}

          {search.trim() === "" &&
            restFiltered.map((c) => (
              <CountryRow
                key={`a-${c.iso}-${c.dial}`}
                country={c}
                selected={selected?.dial === c.dial && selected?.iso === c.iso}
                onSelect={() => handleSelect(c.dial)}
                innerRef={
                  selected?.dial === c.dial && selected?.iso === c.iso
                    ? selectedRef
                    : null
                }
              />
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

CountryCodeSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

function SectionHeader({ label }) {
  return (
    <p className="sticky top-0 z-10 bg-popover px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
  );
}

SectionHeader.propTypes = {
  label: PropTypes.string.isRequired,
};

function CountryRow({ country, selected, onSelect, innerRef }) {
  return (
    <button
      ref={innerRef}
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
        selected && "bg-accent",
      )}
    >
      <span className="text-lg leading-none">{getFlagEmoji(country.iso)}</span>
      <span className="min-w-0 flex-1 truncate">{country.name}</span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {country.dial}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0" />}
    </button>
  );
}

CountryRow.propTypes = {
  country: PropTypes.shape({
    iso: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    dial: PropTypes.string.isRequired,
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  innerRef: PropTypes.object,
};

/**
 * Combined country-code selector + national number input.
 * `value` is the national number (digits only), `countryCode` is "+91" style.
 * `onChange` always receives the digit-only national number string.
 */
export function PhoneInput({
  value,
  onChange,
  countryCode,
  onCountryCodeChange,
  placeholder = "Enter phone number",
  disabled = false,
  id,
  className,
  inputClassName,
  codeClassName,
}) {
  const handleNumberChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
    onChange?.(digits);
  };

  return (
    <div className={cn("flex gap-2", className)}>
      <CountryCodeSelect
        value={countryCode || DEFAULT_COUNTRY_CODE}
        onChange={onCountryCodeChange}
        disabled={disabled}
        className={codeClassName}
      />
      <Input
        id={id}
        value={value ?? ""}
        onChange={handleNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        inputMode="tel"
        autoComplete="tel"
        className={cn("h-11 flex-1 sm:h-8", inputClassName)}
      />
    </div>
  );
}

PhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  countryCode: PropTypes.string,
  onCountryCodeChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  codeClassName: PropTypes.string,
};
